import type {
  Guest,
  GuestStats,
  CreateGuestInput,
  UpdateGuestInput,
  UpdateRsvpInput,
  BulkCreateGuestsInput,
  BulkUpdateGuestsInput,
  BulkDeleteGuestsInput,
  BulkMutationResult,
} from '@peles/shared';
import { apiClient } from './client';

export interface GuestFilters {
  eventId?: string;
  /** Multi-select; serialized as repeated `side[]` query params. */
  side?: string[];
  /** Multi-select; serialized as repeated `rsvpStatus[]` query params. */
  rsvpStatus?: string[];
  /** Multi-select; serialized as repeated `familyGroup[]` query params. */
  familyGroup?: string[];
}

export async function getGuests(filters?: GuestFilters): Promise<Guest[]> {
  // Multi-value arrays serialize as `?side[]=a&side[]=b` via axios's default
  // params serializer, which the backend's qs query parser collapses back
  // to `req.query.side = ['a', 'b']`.
  const params: Record<string, string | string[]> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.side?.length) params.side = filters.side;
  if (filters?.rsvpStatus?.length) params.rsvpStatus = filters.rsvpStatus;
  if (filters?.familyGroup?.length) params.familyGroup = filters.familyGroup;
  const { data } = await apiClient.get<Guest[]>('/guests', { params });
  return data;
}

export async function getGuest(id: string): Promise<Guest> {
  const { data } = await apiClient.get<Guest>(`/guests/${id}`);
  return data;
}

export async function getGuestStats(eventId: string): Promise<GuestStats> {
  const { data } = await apiClient.get<GuestStats>('/guests/stats', {
    params: { eventId },
  });
  return data;
}

export async function getGuestFamilyGroups(eventId?: string): Promise<string[]> {
  const params: Record<string, string> = {};
  if (eventId) params.eventId = eventId;
  const { data } = await apiClient.get<string[]>('/guests/family-groups', { params });
  return data;
}

export async function createGuest(input: CreateGuestInput): Promise<Guest> {
  const { data } = await apiClient.post<Guest>('/guests', input);
  return data;
}

export async function updateGuest(id: string, updates: UpdateGuestInput): Promise<Guest> {
  const { data } = await apiClient.patch<Guest>(`/guests/${id}`, updates);
  return data;
}

export async function deleteGuest(id: string): Promise<void> {
  await apiClient.delete(`/guests/${id}`);
}

export async function updateRsvp(id: string, input: UpdateRsvpInput): Promise<Guest> {
  const { data } = await apiClient.patch<Guest>(`/guests/${id}/rsvp`, input);
  return data;
}

export async function bulkCreateGuests(input: BulkCreateGuestsInput): Promise<Guest[]> {
  const { data } = await apiClient.post<Guest[]>('/guests/bulk', input);
  return data;
}

export async function bulkUpdateGuests(input: BulkUpdateGuestsInput): Promise<BulkMutationResult> {
  const { data } = await apiClient.post<BulkMutationResult>('/guests/bulk-update', input);
  return data;
}

export async function bulkDeleteGuests(input: BulkDeleteGuestsInput): Promise<BulkMutationResult> {
  const { data } = await apiClient.post<BulkMutationResult>('/guests/bulk-delete', input);
  return data;
}
